import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";
import { useDialogStack } from "@/shared/hooks/useDialogStack";
import { useIsMobile } from "@/v2/hooks/useIsMobile";
import { cn } from "@/v2/lib/utils";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "./tooltip";

const ResponsiveDialog = DialogPrimitive.Root;

const ResponsiveDialogTrigger = DialogPrimitive.Trigger;

const ResponsiveDialogClose = DialogPrimitive.Close;

const ResponsiveDialogPortal = DialogPrimitive.Portal;

const ResponsiveDialogOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn("fixed inset-0 z-50 bg-black/60", className)}
		{...props}
	/>
));
ResponsiveDialogOverlay.displayName = "ResponsiveDialogOverlay";

interface ResponsiveDialogContentProps
	extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
	title: string;
	subtitle?: string;
	footer?: React.ReactNode;
	/** Actions rendered in the header bar before the close button. */
	headerActions?: React.ReactNode;
	/** Override the desktop header wrapper className. */
	headerClassName?: string;
	/** Override the body wrapper className. Defaults to `min-h-0 flex-1 overflow-y-auto` (scrollable). Prefer portaled popovers/dropdowns over `overflow-visible` so footers stay pinned inside constrained dialogs. */
	bodyClassName?: string;
}

type DialogContentProps = React.ComponentPropsWithoutRef<
	typeof DialogPrimitive.Content
>;
type PointerDownOutsideEvent = Parameters<
	NonNullable<DialogContentProps["onPointerDownOutside"]>
>[0];
type InteractOutsideEvent = Parameters<
	NonNullable<DialogContentProps["onInteractOutside"]>
>[0];

const ResponsiveDialogContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	ResponsiveDialogContentProps
>(
	(
		{
			className,
			title,
			subtitle,
			footer,
			headerActions,
			headerClassName,
			bodyClassName,
			children,
			onOpenAutoFocus,
			onEscapeKeyDown,
			onPointerDownOutside,
			onInteractOutside,
			tabIndex,
			...props
		},
		ref,
	) => {
		const isMobile = useIsMobile();
		const { isTopDialog } = useDialogStack();
		const contentRef = React.useRef<React.ElementRef<
			typeof DialogPrimitive.Content
		> | null>(null);
		const setContentRef = React.useCallback(
			(node: React.ElementRef<typeof DialogPrimitive.Content> | null) => {
				contentRef.current = node;
				if (typeof ref === "function") {
					ref(node);
				} else if (ref) {
					(
						ref as React.MutableRefObject<
							React.ElementRef<typeof DialogPrimitive.Content> | null
						>
					).current = node;
				}
			},
			[ref],
		);

		const isInteractivePortalEvent = React.useCallback((event: Event) => {
			const target = event.target;
			return (
				target instanceof Element &&
				Boolean(
					target.closest(
						'[data-dialog-portal-interactive="true"], [data-radix-popper-content-wrapper]',
					),
				)
			);
		}, []);

		const getOriginalEvent = React.useCallback((event: Event): Event => {
			return (
				(event as CustomEvent<{ originalEvent?: Event }>).detail?.originalEvent ??
				event
			);
		}, []);

		const handleOpenAutoFocus = React.useCallback(
			(event: Event) => {
				onOpenAutoFocus?.(event);
				if (event.defaultPrevented) return;

				// Keep initial focus inside the dialog without jumping to the first
				// field/control. This lets keyboard events belong to the active modal
				// immediately after it opens.
				event.preventDefault();
				contentRef.current?.focus({ preventScroll: true });
			},
			[onOpenAutoFocus],
		);

		// Don't close the dialog on Escape if a dropdown/popover is open inside,
		// or if another dialog is open on top of this one.
		const handleEscapeKeyDown = React.useCallback(
			(e: KeyboardEvent) => {
				if (!isTopDialog()) {
					e.preventDefault();
					return;
				}
				const hasOpenDropdown = document.querySelector(
					'[role="listbox"], [data-radix-popper-content-wrapper]',
				);
				if (hasOpenDropdown) e.preventDefault();
				onEscapeKeyDown?.(e);
			},
			[isTopDialog, onEscapeKeyDown],
		);

		const handlePointerDownOutside = React.useCallback(
			(e: PointerDownOutsideEvent) => {
				onPointerDownOutside?.(e);
				if (e.defaultPrevented) return;

				// The entry form uses portaled dropdowns/popovers so they are not
				// clipped by the scrollable dialog body. They are visually part of the
				// dialog, so clicking them must not count as an outside interaction.
				if (isInteractivePortalEvent(getOriginalEvent(e))) e.preventDefault();
			},
			[onPointerDownOutside, isInteractivePortalEvent, getOriginalEvent],
		);

		const handleInteractOutside = React.useCallback(
			(e: InteractOutsideEvent) => {
				onInteractOutside?.(e);
				if (e.defaultPrevented) return;

				if (isInteractivePortalEvent(getOriginalEvent(e))) e.preventDefault();
			},
			[onInteractOutside, isInteractivePortalEvent, getOriginalEvent],
		);

		if (isMobile) {
			return (
				<ResponsiveDialogPortal>
					<DialogPrimitive.Content
						ref={setContentRef}
						tabIndex={tabIndex ?? -1}
						className={cn(
							"fixed inset-0 z-50 flex flex-col bg-background focus:outline-none",
							"data-[state=open]:animate-in data-[state=closed]:animate-out",
							"data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
							"duration-200",
							className,
						)}
						onOpenAutoFocus={handleOpenAutoFocus}
						onEscapeKeyDown={handleEscapeKeyDown}
						onPointerDownOutside={handlePointerDownOutside}
						onInteractOutside={handleInteractOutside}
						{...props}
					>
						{/* Mobile header */}
						<div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4 gap-2">
							<DialogPrimitive.Title className="text-base font-medium">
								{title}
							</DialogPrimitive.Title>
							{headerActions && (
								<div className="flex items-center gap-2 ml-auto">
									{headerActions}
								</div>
							)}
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger asChild>
										<DialogPrimitive.Close className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors shrink-0">
											<X className="size-5" />
											<span className="sr-only">Close</span>
										</DialogPrimitive.Close>
									</TooltipTrigger>
									<TooltipContent side="bottom">Close</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</div>

						{/* Mobile body */}
						<div
							className={cn("min-h-0 flex-1 overflow-y-auto", bodyClassName)}
						>
							{children}
						</div>

						{/* Mobile footer */}
						{footer && (
							<div className="shrink-0 border-t border-border p-4">
								{footer}
							</div>
						)}

						<DialogPrimitive.Description className="sr-only">
							{subtitle ?? title}
						</DialogPrimitive.Description>
					</DialogPrimitive.Content>
				</ResponsiveDialogPortal>
			);
		}

		return (
			<ResponsiveDialogPortal>
				{/* Keep desktop content inside the overlay's RemoveScroll tree.
						This is the Radix-safe shape for scrollable, portaled popovers
						inside modal dialogs. */}
				<ResponsiveDialogOverlay>
						<DialogPrimitive.Content
							ref={setContentRef}
							tabIndex={tabIndex ?? -1}
							className={cn(
								"fixed left-1/2 top-1/2 z-50 w-full max-w-lg max-h-[85vh] -translate-x-1/2 -translate-y-1/2 bg-background shadow-lg focus:outline-none sm:rounded-lg",
								footer !== undefined
									? "flex flex-col overflow-hidden"
									: "grid gap-4 overflow-y-auto p-6",
								className,
							)}
							onOpenAutoFocus={handleOpenAutoFocus}
							onEscapeKeyDown={handleEscapeKeyDown}
							onPointerDownOutside={handlePointerDownOutside}
							onInteractOutside={handleInteractOutside}
							{...props}
						>
							{footer !== undefined ? (
								<>
									{/* Desktop header */}
									<div
										className={cn(
											"flex items-start justify-between gap-3 p-6 pb-2 pr-12 sm:text-left",
											headerClassName,
										)}
									>
										<div className="flex flex-col space-y-1.5">
											<DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
												{title}
											</DialogPrimitive.Title>
											{subtitle && (
												<DialogPrimitive.Description className="text-sm text-muted-foreground">
													{subtitle}
												</DialogPrimitive.Description>
											)}
										</div>
										{headerActions && (
											<div className="shrink-0">{headerActions}</div>
										)}
									</div>

									{/* Desktop body — scrollable */}
									<div
										className={cn(
											"min-h-0 flex-1 overflow-y-auto",
											bodyClassName,
										)}
									>
										{children}
									</div>

									{/* Desktop footer — pinned, only when truthy */}
									{footer && (
										<div className="shrink-0 border-t border-border p-4">
											{footer}
										</div>
									)}
								</>
							) : (
								<>
									{/* Desktop header */}
									<div
										className={cn(
											"flex items-start justify-between gap-3 pr-8 sm:text-left",
											headerClassName,
										)}
									>
										<div className="flex flex-col space-y-1.5">
											<DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
												{title}
											</DialogPrimitive.Title>
											{subtitle && (
												<DialogPrimitive.Description className="text-sm text-muted-foreground">
													{subtitle}
												</DialogPrimitive.Description>
											)}
										</div>
										{headerActions && (
											<div className="shrink-0">{headerActions}</div>
										)}
									</div>

									{/* Desktop body */}
									{children}
								</>
							)}

							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger asChild>
										<DialogPrimitive.Close className="absolute right-4 top-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
											<X className="h-4 w-4" />
											<span className="sr-only">Close</span>
										</DialogPrimitive.Close>
									</TooltipTrigger>
									<TooltipContent side="bottom">Close</TooltipContent>
								</Tooltip>
							</TooltipProvider>

							{!subtitle && (
								<DialogPrimitive.Description className="sr-only">
									{title}
								</DialogPrimitive.Description>
							)}
						</DialogPrimitive.Content>
				</ResponsiveDialogOverlay>
			</ResponsiveDialogPortal>
		);
	},
);
ResponsiveDialogContent.displayName = "ResponsiveDialogContent";

export {
	ResponsiveDialog,
	ResponsiveDialogTrigger,
	ResponsiveDialogContent,
	ResponsiveDialogClose,
};
